import { captureException } from '@sentry/client';
import { Component, type PropsWithChildren } from 'react';
import Styled from './ErrorBoundary.styled';

interface Props extends PropsWithChildren {
  hideText?: boolean;
  onCatchError?: () => void;
}

interface State {
  errorMessage: string;
  errorStack: string;
  hideText?: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      errorMessage: '',
      errorStack: '',
      hideText: props.hideText
    };
  }

  componentDidCatch(error: unknown, errorInfo: unknown) {
    // Log error to Sentry
    captureException(error);
    // Run callback, if available
    this.props.onCatchError?.();

    if (import.meta.env.DEV) {
      const errorMessage =
        error instanceof Error ? error.message : error.toString();
      const errorStack =
        typeof errorInfo === 'object' && 'componentStack' in errorInfo
          ? errorInfo.componentStack.toString()
          : '';

      this.setState({
        errorMessage,
        errorStack
      });
    }
  }

  render() {
    if (this.state.errorMessage) {
      return (
        <Styled.Wrapper>
          {!this.state.hideText && (
            <>
              <Styled.Header>Something went wrong.</Styled.Header>
              <Styled.Text>
                Please refresh the page or try again later.
              </Styled.Text>
            </>
          )}
          <Styled.ErrorMessage>
            <Styled.ErrorTitle>ERROR</Styled.ErrorTitle>
            {this.state.errorMessage}
            {this.state.errorStack}
          </Styled.ErrorMessage>
        </Styled.Wrapper>
      );
    }

    // Render children normally if no error occurred
    return this.props.children;
  }
}

export default ErrorBoundary;
